import type { ISuccessApiResponse } from '@wyz/types';
import {
  UseMutationOptions,
  invariant,
  restrictApi,
  serialize,
  useMutation,
} from '@wyz/utils';
import { useApi, useConfig } from '~/common/context';
import {
  IBaseSearchFormSchema,
  IQuoteContactEditPayload,
  IQuoteCreatedResponse,
  IQuoteEditPayload,
  IQuoteErrorResponse,
} from '~/purchase/types';
import { PurchaseQueryKeys, prepareSearchQuery } from '~/purchase/api';
import { SEARCH_TYPE_ENUM } from '~/purchase/schemas';
import { isNil, omitBy } from 'lodash-es';

type IQuoteVariables = {
  customerId: string;
  reference: string;
};

type Options = {
  resendMail?: UseMutationOptions<
    ISuccessApiResponse,
    IQuoteErrorResponse,
    IQuoteVariables & { withLabels?: boolean }
  >;
  duplicate?: UseMutationOptions<
    ISuccessApiResponse,
    IQuoteErrorResponse,
    IQuoteVariables
  >;
  edit?: UseMutationOptions<
    ISuccessApiResponse,
    IQuoteErrorResponse,
    { body: IQuoteEditPayload } & IQuoteVariables
  >;
  editContact?: UseMutationOptions<
    ISuccessApiResponse,
    IQuoteErrorResponse,
    { body: IQuoteContactEditPayload } & IQuoteVariables
  >;
  createExpress?: UseMutationOptions<
    ISuccessApiResponse<IQuoteCreatedResponse>,
    IQuoteErrorResponse,
    { customerId: string; searchQuery: IBaseSearchFormSchema }
  >;
};

export const useManageQuotes = <
  TRequestedKeys extends keyof Options,
  TRequestedOptions extends Options = Pick<Options, TRequestedKeys>,
>(
  options: TRequestedOptions,
) => {
  const { tenantCode } = useConfig();
  const { callCoreApi } = useApi();

  const resendMailMutation = useMutation({
    mutationFn: (variables) =>
      callCoreApi<ISuccessApiResponse>(
        `/offers/api/tenants/${tenantCode}/customers/${variables.customerId}/quotes/${variables.reference}/resend-email${
          variables.withLabels ? '?labels=1' : ''
        }`,
        { method: 'POST' },
      ),
    retry: false,
    keysToInvalidateOnSuccess: (variable) => [
      PurchaseQueryKeys.quotes.paginate(variable.customerId),
    ],
    ...options.resendMail,
  });

  const duplicateMutation = useMutation({
    mutationFn: (variables) =>
      callCoreApi<ISuccessApiResponse>(
        `/offers/api/tenants/${tenantCode}/customers/${variables.customerId}/quotes/${variables.reference}/duplicate`,
        { method: 'POST' },
      ),
    retry: false,
    keysToInvalidateOnSuccess: (variable) => [
      PurchaseQueryKeys.quotes.paginate(variable.customerId),
    ],
    ...options.duplicate,
  });

  const editMutation = useMutation({
    mutationFn: ({ customerId, reference, body }) =>
      callCoreApi<ISuccessApiResponse>(
        `/offers/api/tenants/${tenantCode}/customers/${customerId}/quotes/${reference}`,
        { method: 'PUT', body: JSON.stringify(omitBy(body, isNil)) },
      ),
    retry: false,
    ...options.edit,
  });

  const editContactMutation = useMutation({
    mutationFn: ({ customerId, reference, body }) =>
      callCoreApi<ISuccessApiResponse>(
        `/offers/api/tenants/${tenantCode}/customers/${customerId}/quotes/${reference}/contact`,
        { method: 'PATCH', body: JSON.stringify(body) },
      ),
    retry: false,
    keysToInvalidateOnSuccess: (variable) => [
      PurchaseQueryKeys.quotes.paginate(variable.customerId),
    ],
    ...options.editContact,
  });

  const createExpressMutation = useMutation({
    mutationFn: ({ customerId, searchQuery }) => {
      const query = prepareSearchQuery(
        SEARCH_TYPE_ENUM.QUOTE,
        'LIST',
        searchQuery,
        customerId,
      );
      invariant(query);

      return callCoreApi<ISuccessApiResponse<IQuoteCreatedResponse>>(
        `/offers/api/tenants/${tenantCode}/customers/${customerId}/quotes/express?${serialize(query)}`,
        { method: 'POST' },
      );
    },
    retry: false,
    keysToInvalidateOnSuccess: (variable) => [
      PurchaseQueryKeys.quotes.paginate(variable.customerId),
    ],
    ...options.createExpress,
  });

  return restrictApi(options, {
    resendMail: resendMailMutation,
    duplicate: duplicateMutation,
    edit: editMutation,
    editContact: editContactMutation,
    createExpress: createExpressMutation,
  });
};
